import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Save,
  Package,
  Image as ImageIcon,
  Search,
  ChevronUp,
  ChevronDown,
  GripVertical
} from 'lucide-react';
import { Product, saveProduct, getProducts, updateProduct, deleteProduct } from '../services/productService';
import { logger } from '../utils/logger';

// Move ProductModal outside to prevent re-creation on every render
const ProductModal: React.FC<{
  isEdit?: boolean;
  formData: any;
  uploadedImage: string | null;
  isUploading: boolean;
  industries: string[];
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onIndustryChange: (industry: string) => void;
  onAddCustomIndustry: (industry: string) => void;
  onRemoveIndustry: (industry: string) => void;
  onMoveIndustry: (index: number, direction: 'up' | 'down') => void;
  onPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPriceBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: () => void;
  onAddProduct: () => void;
  onUpdateProduct: () => void;
  onClose: () => void;
}> = ({ 
  isEdit = false, 
  formData, 
  uploadedImage, 
  isUploading, 
  industries, 
  onInputChange,
  onIndustryChange,
  onAddCustomIndustry,
  onRemoveIndustry,
  onMoveIndustry,
  onPriceChange,
  onPriceBlur,
  onFileUpload, 
  onRemoveImage, 
  onAddProduct, 
  onUpdateProduct, 
  onClose 
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={onInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              placeholder="e.g., Poynt C Terminal"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Supported Industries * (Select multiple)
            </label>
            
            {/* Selected Industries - Reorderable */}
            {formData.industries.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-gray-600 mb-2">Selected Industries (drag to reorder):</p>
                <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                  {formData.industries.map((industry: string, index: number) => (
                    <div key={`${industry}-${index}`} className="flex items-center gap-2 mb-2 p-2 bg-white rounded border border-gray-200">
                      <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="flex-1 text-sm text-gray-700">{industry}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => onMoveIndustry(index, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onMoveIndustry(index, 'down')}
                          disabled={index === formData.industries.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemoveIndustry(industry)}
                          className="p-1 text-red-400 hover:text-red-600"
                          title="Remove"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Custom Industry */}
            <div className="mb-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  id="newIndustry"
                  placeholder="Add custom industry..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      if (input.value.trim() && !formData.industries.includes(input.value.trim())) {
                        onAddCustomIndustry(input.value.trim());
                        input.value = '';
                      }
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const input = document.getElementById('newIndustry') as HTMLInputElement;
                    if (input && input.value.trim() && !formData.industries.includes(input.value.trim())) {
                      onAddCustomIndustry(input.value.trim());
                      input.value = '';
                    }
                  }}
                  className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Available Industries - Checkboxes */}
            <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
              <p className="text-xs text-gray-600 mb-2">Available Industries:</p>
              {industries.filter(industry => !formData.industries.includes(industry)).map(industry => (
                <label key={industry} className="flex items-center mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => onIndustryChange(industry)}
                    className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{industry}</span>
                </label>
              ))}
              {industries.filter(industry => !formData.industries.includes(industry)).length === 0 && (
                <p className="text-xs text-gray-500 italic">All industries selected</p>
              )}
            </div>
            
            {formData.industries.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please select or add at least one industry</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Category *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={onInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            >
              <option value="pos-systems">POS Systems</option>
              <option value="mobile-solutions">Mobile Solutions</option>
              <option value="online-payments">Online Payments</option>
              <option value="handheld-terminals">Handheld Terminals</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={onInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              placeholder="Enter a detailed description of the product (optional)..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Button Action *
            </label>
            <select
              name="actionType"
              value={formData.actionType}
              onChange={onInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            >
              <option value="quote">Get Quote</option>
              <option value="buy">Buy</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">This determines what button text appears on the product card</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price * (Multiple prices allowed, separate with commas)
            </label>
            <input
              type="text"
              name="price"
              value={formData.price}
              onChange={onPriceChange}
              onBlur={onPriceBlur}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              placeholder="e.g., 299, 299.90, or 299, 399"
            />
            <p className="text-xs text-gray-500 mt-1">Enter numbers (e.g., 299 or 299.90). $ and .00 will be added automatically</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Image
            </label>
            
            {/* File Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              {uploadedImage ? (
                <div className="space-y-4">
                  <div className="relative inline-block">
                    <img 
                      src={uploadedImage} 
                      alt="Product preview" 
                      className="max-h-32 max-w-full rounded-lg shadow-sm"
                    />
                    <button
                      type="button"
                      onClick={onRemoveImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">Image uploaded successfully</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="mx-auto w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="text-gray-600 hover:text-gray-700 font-medium">
                        Click to upload
                      </span>
                      <span className="text-gray-600"> or drag and drop</span>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      onChange={onFileUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF up to 5MB
                  </p>
                  {isUploading && (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      <span className="text-sm text-gray-600">Uploading...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="warranty"
              checked={formData.warranty}
              onChange={onInputChange}
              className="h-4 w-4 text-gray-600 focus:ring-gray-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900">
              1 Year Warranty Included
            </label>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={isEdit ? onUpdateProduct : onAddProduct}
              className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isEdit ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);

const ProductManagement: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [prefixFilter, setPrefixFilter] = useState<string>('all'); // 'all', 'VP', 'VL'

  const [formData, setFormData] = useState({
    name: '',
    industries: [] as string[], // Changed to array for multiple selection
    price: '',
    warranty: true,
    image: '',
    category: 'pos-systems' as 'pos-systems' | 'mobile-solutions' | 'online-payments' | 'handheld-terminals',
    description: '',
    actionType: 'quote' as 'buy' | 'quote'
  });

  const industries = [
    'Retail',
    'Restaurant',
    'Service',
    'Healthcare',
    'Beauty & Wellness',
    'Automotive',
    'Real Estate',
    'Events',
    'Mobile Sales',
    'Pop-up',
    'E-commerce',
    'Other'
  ];

  // Load products from Firestore on component mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        const productsData = await getProducts();
        setProducts(productsData);
      } catch (error) {
        logger.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleIndustryChange = (industry: string) => {
    setFormData(prev => {
      if (prev.industries.includes(industry)) {
        // Already selected, do nothing (removal is handled by onRemoveIndustry)
        return prev;
      }
      // Add to the end
      return { ...prev, industries: [...prev.industries, industry] };
    });
  };

  const handleAddCustomIndustry = (industry: string) => {
    if (industry.trim() && !formData.industries.includes(industry.trim())) {
      setFormData(prev => ({
        ...prev,
        industries: [...prev.industries, industry.trim()]
      }));
    }
  };

  const handleRemoveIndustry = (industry: string) => {
    setFormData(prev => ({
      ...prev,
      industries: prev.industries.filter(i => i !== industry)
    }));
  };

  const handleMoveIndustry = (index: number, direction: 'up' | 'down') => {
    setFormData(prev => {
      const newIndustries = [...prev.industries];
      if (direction === 'up' && index > 0) {
        [newIndustries[index - 1], newIndustries[index]] = [newIndustries[index], newIndustries[index - 1]];
      } else if (direction === 'down' && index < newIndustries.length - 1) {
        [newIndustries[index], newIndustries[index + 1]] = [newIndustries[index + 1], newIndustries[index]];
      }
      return { ...prev, industries: newIndustries };
    });
  };

  const formatPrice = (value: string): string => {
    if (!value) return '';
    
    // Handle multiple prices separated by commas
    const prices = value.split(',').map(price => {
      // Remove any existing $ and trim whitespace
      let cleaned = price.trim().replace(/^\$/, '').trim();
      
      // If empty, return as is
      if (!cleaned) return price.trim();
      
      // Check if it's a number (with optional decimal)
      const numberMatch = cleaned.match(/^(\d+)(\.\d+)?$/);
      if (numberMatch) {
        const wholePart = numberMatch[1];
        const decimalPart = numberMatch[2];
        
        // If no decimal part, add .00
        if (!decimalPart) {
          cleaned = wholePart + '.00';
        }
        
        // Add $ prefix
        return '$' + cleaned;
      }
      
      // If it's not a simple number (e.g., "Free", "$49/month"), return as is
      return price.trim();
    });
    
    return prices.join(', ');
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Store the raw value, formatting will happen on blur or save
    setFormData(prev => ({
      ...prev,
      price: value
    }));
  };

  const handlePriceBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Format the price when user leaves the field
    const formatted = formatPrice(e.target.value);
    setFormData(prev => ({
      ...prev,
      price: formatted
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);

    try {
      // Convert file to base64 for preview and storage
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        setUploadedImage(result);
        setFormData(prev => ({
          ...prev,
          image: result // Store base64 data
        }));
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      logger.error('Error uploading file:', error);
      alert('Error uploading file. Please try again.');
      setIsUploading(false);
    }
  };

  const removeUploadedImage = () => {
    setUploadedImage(null);
    setFormData(prev => ({
      ...prev,
      image: ''
    }));
  };

  const handleAddProduct = async () => {
    if (!formData.name || formData.industries.length === 0 || !formData.price) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Format price before saving
      const formattedPrice = formatPrice(formData.price);
      
      const newProduct = await saveProduct({
        name: formData.name,
        industry: formData.industries, // Store as array
        price: formattedPrice,
        warranty: formData.warranty,
        image: formData.image || '📦',
        category: formData.category,
        description: formData.description || '', // Optional
        actionType: formData.actionType
      });

      setProducts(prev => [newProduct, ...prev]);
      setFormData({ 
        name: '', 
        industries: [], 
        price: '', 
        warranty: true, 
        image: '', 
        category: 'pos-systems',
        description: '',
        actionType: 'quote'
      });
      setUploadedImage(null);
      setShowAddModal(false);
      
      logger.log('New product added:', newProduct);
    } catch (error) {
      logger.error('Error adding product:', error);
      alert('Error adding product. Please try again.');
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    // Handle both single industry (string) and multiple industries (array) for backward compatibility
    const industries = Array.isArray(product.industry) 
      ? product.industry 
      : product.industry ? [product.industry] : [];
    
    setFormData({
      name: product.name,
      industries: industries,
      price: product.price,
      warranty: product.warranty,
      image: product.image,
      category: product.category || 'pos-systems',
      description: product.description || '',
      actionType: product.actionType || 'quote'
    });
    // Set uploaded image if it's a base64 string
    if (product.image.startsWith('data:image/')) {
      setUploadedImage(product.image);
    } else {
      setUploadedImage(null);
    }
    setShowEditModal(true);
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct || !formData.name || formData.industries.length === 0 || !formData.price) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      // Format price before saving
      const formattedPrice = formatPrice(formData.price);
      
      await updateProduct(selectedProduct.id, {
        name: formData.name,
        industry: formData.industries, // Store as array
        price: formattedPrice,
        warranty: formData.warranty,
        image: formData.image || '📦',
        category: formData.category,
        description: formData.description || '', // Optional
        actionType: formData.actionType
      });

      const updatedProduct: Product = {
        ...selectedProduct,
        name: formData.name,
        industry: formData.industries,
        price: formattedPrice,
        warranty: formData.warranty,
        image: formData.image || '📦',
        category: formData.category,
        description: formData.description || '',
        actionType: formData.actionType
      };

      setProducts(prev => prev.map(p => p.id === selectedProduct.id ? updatedProduct : p));
      setShowEditModal(false);
      setSelectedProduct(null);
      setFormData({ 
        name: '', 
        industries: [], 
        price: '', 
        warranty: true, 
        image: '', 
        category: 'pos-systems',
        description: '',
        actionType: 'quote'
      });
      setUploadedImage(null);
      
      logger.log('Product updated:', updatedProduct);
    } catch (error) {
      logger.error('Error updating product:', error);
      alert('Error updating product. Please try again.');
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(productId);
        setProducts(prev => prev.filter(p => p.id !== productId));
        logger.log('Product deleted:', productId);
      } catch (error) {
        logger.error('Error deleting product:', error);
        alert('Error deleting product. Please try again.');
      }
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Product Management</h2>
          <p className="text-gray-600">Manage your POS products and pricing</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Product
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Box */}
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-gray-500 focus:border-gray-500 sm:text-sm"
            placeholder="Search products by name..."
          />
        </div>

        {/* Prefix Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => setPrefixFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              prefixFilter === 'all'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setPrefixFilter('VP')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              prefixFilter === 'VP'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            VP
          </button>
          <button
            onClick={() => setPrefixFilter('VL')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              prefixFilter === 'VL'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            VL
          </button>
        </div>
      </div>

      {/* Products Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
          <span className="ml-3 text-gray-600">Loading products...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products
            .filter(product => {
              // Filter by search query
              const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
              
              // Filter by prefix
              let matchesPrefix = true;
              if (prefixFilter !== 'all') {
                matchesPrefix = product.name.toUpperCase().startsWith(prefixFilter);
              }
              
              return matchesSearch && matchesPrefix;
            })
            .sort((a, b) => {
              // Sort: VP products first, then VL products, then others
              const aPrefix = a.name.toUpperCase().startsWith('VP') ? 0 : a.name.toUpperCase().startsWith('VL') ? 1 : 2;
              const bPrefix = b.name.toUpperCase().startsWith('VP') ? 0 : b.name.toUpperCase().startsWith('VL') ? 1 : 2;
              
              if (aPrefix !== bPrefix) {
                return aPrefix - bPrefix;
              }
              
              // If same prefix, sort alphabetically
              return a.name.localeCompare(b.name);
            })
            .map((product) => (
          <div key={product.id} className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
            {/* Product Image */}
            <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-50 flex items-center justify-center">
              {product.image.startsWith('data:image/') ? (
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : product.image.startsWith('/') ? (
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <div className="text-6xl">{product.image}</div>
              )}
            </div>

            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                {product.warranty && (
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                    1 Year Warranty
                  </span>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Industries:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {Array.isArray(product.industry) 
                      ? product.industry.join(', ') 
                      : product.industry || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Category:</span>
                  <span className="text-sm font-medium text-gray-600">{product.category || 'POS Systems'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Price:</span>
                  <span className="text-sm font-medium text-gray-600 font-semibold">{product.price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Added:</span>
                  <span className="text-sm text-gray-500">{product.createdAt.toLocaleDateString()}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEditProduct(product)}
                  className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                >
                  <Edit className="h-3 w-3" />
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteProduct(product.id)}
                  className="flex-1 bg-red-100 text-red-700 px-3 py-2 rounded text-sm hover:bg-red-200 transition-colors flex items-center justify-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* Empty State */}
      {!isLoading && products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
        let matchesPrefix = true;
        if (prefixFilter !== 'all') {
          matchesPrefix = product.name.toUpperCase().startsWith(prefixFilter);
        }
        return matchesSearch && matchesPrefix;
      }).length === 0 && (
        <div className="text-center py-12">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products yet</h3>
          <p className="text-gray-600 mb-4">Get started by adding your first product</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Add Your First Product
          </button>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <ProductModal
          isEdit={false}
          formData={formData}
          uploadedImage={uploadedImage}
          isUploading={isUploading}
          industries={industries}
          onInputChange={handleInputChange}
          onIndustryChange={handleIndustryChange}
          onAddCustomIndustry={handleAddCustomIndustry}
          onRemoveIndustry={handleRemoveIndustry}
          onMoveIndustry={handleMoveIndustry}
          onPriceChange={handlePriceChange}
          onPriceBlur={handlePriceBlur}
          onFileUpload={handleFileUpload}
          onRemoveImage={removeUploadedImage}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setSelectedProduct(null);
            setFormData({ 
              name: '', 
              industries: [], 
              price: '', 
              warranty: true, 
              image: '', 
              category: 'pos-systems',
              description: '',
              actionType: 'quote'
            });
            setUploadedImage(null);
          }}
        />
      )}
      {showEditModal && (
        <ProductModal
          isEdit={true}
          formData={formData}
          uploadedImage={uploadedImage}
          isUploading={isUploading}
          industries={industries}
          onInputChange={handleInputChange}
          onIndustryChange={handleIndustryChange}
          onAddCustomIndustry={handleAddCustomIndustry}
          onRemoveIndustry={handleRemoveIndustry}
          onMoveIndustry={handleMoveIndustry}
          onPriceChange={handlePriceChange}
          onPriceBlur={handlePriceBlur}
          onFileUpload={handleFileUpload}
          onRemoveImage={removeUploadedImage}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setSelectedProduct(null);
            setFormData({ 
              name: '', 
              industries: [], 
              price: '', 
              warranty: true, 
              image: '', 
              category: 'pos-systems',
              description: '',
              actionType: 'quote'
            });
            setUploadedImage(null);
          }}
        />
      )}
    </div>
  );
};

export default ProductManagement;
